/* eslint-disable no-undef */
import * as Sentry from '@sentry/react';
import TransitionModal from 'components/clientLogin/components/TransitionModal';
import { freshChatConfig } from 'config/freshchat';
import { ConnectedRouter, push } from 'connected-react-router';
import { createNewActivity } from 'constants/api/v1';
import { useFirebaseAuth } from 'hooks/useFirebaseAuth';
import { useGoogleMapsKey } from 'hooks/useGoogleMapsKey';
import { usePostHogIdentify } from 'hooks/usePostHogIdentify';
import { usePageTracking } from 'hooks/usePageTracking';
import { useDocumentTitle } from 'hooks/useDocumentTitle';
import { isEmpty } from 'lodash';
import moment from 'moment-timezone';
import * as Common from '@nimbly-technologies/nimbly-common';
import React, { useMemo, useState } from 'react';
import 'react-dates/initialize';
import 'react-dates/lib/css/_datepicker.css';
import { isMobile } from 'react-device-detect';
import FreshChat from 'react-freshchat';
import ReactGA from 'react-ga';
import { InferableComponentEnhancerWithProps, connect, useSelector } from 'react-redux';
import { Route, Switch } from 'react-router';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.min.css';
import { fetchFeatureAccess, resetFeatureAccess } from 'reducers/featureAccess/featureAccess.action';
import { fetchOrganizations, resetOrganization } from 'reducers/organizations/organizations.actions';
import { setEnrollmentStatus } from 'reducers/userEnrollment/userEnrollment.action';
import { LMS_ROUTE } from 'routes/LmsRoutes';
import { RootState } from 'store/rootReducers';
import { isActiveOrganization } from 'utils/organization';
import { identifyUser, logErrorWithContext } from 'utils/sentry/sentrySetup';
import { version } from '../package.json';
import './App.css';
import FreezeAccountModal from './components/FreezeAccountModal/FreezeAccountModal';
import AsyncComponent from './components/global/AsyncComponent/AsyncComponent';
import LoadingPage from './pages/loading';
import LoginPage from './pages/login';
import NotFound from './pages/notfound';
import SetPasswordPage from './pages/setpassword';
import { setDummyState } from './reducers/account';
import { setUserIsSandbox } from './reducers/user/user.action';
import { setUserAccessPermission, resetUserAccessState } from './reducers/useraccess';
import { history } from './store/configStore';
import { RejectedSessionKey } from './constants/errorKeys';
import { isLoginRoute, isSetPasswordRoute, isSystemRoute } from './utils/session/authRoutes';
import { endRejectedSession } from './utils/session/rejectedSession';
import { evaluateSession } from './utils/session/sessionGate';
import AppErrorBoundary from './components/global/AppErrorBoundary/AppErrorBoundary';

import { selectProfile } from './store/selectors/profile';
import { clearProfile, fetchProfileAsync } from 'reducers/profile/profile.action';
const AdminRoute = AsyncComponent(() => import('./routes/admin-routes'));
const SuperadminRoute = AsyncComponent(() => import('./routes/superadmin-routes'));

/**
 * Validates and decodes a redirect path.
 * Prevents open redirect vulnerabilities by ensuring:
 * - Path is relative (starts with /)
 * - Path doesn't contain external protocols
 * - Path doesn't redirect to system pages (login/setpassword/loading)
 * @param path - The redirect path to validate
 * @returns The decoded, safe path if valid, otherwise null.
 */
const isValidRedirectPath = (path: string): string | null => {
	try {
		const decodedPath = decodeURIComponent(path);

		// Must be a relative path starting with /
		if (!decodedPath.startsWith('/')) {
			return null;
		}

		// Should not redirect to system pages to prevent redirect loops
		if (isSystemRoute(decodedPath)) {
			return null;
		}

		// Should not contain protocol to prevent external redirects (security)
		if (decodedPath.includes('://') || decodedPath.startsWith('//')) {
			return null;
		}

		return decodedPath;
	} catch {
		// If decoding fails, reject the path
		return null;
	}
};

interface GAAttribute {
	userId?: string;
	dimension1?: string; // org
	dimension2?: string; // version
	dimension3?: string; // uid
	dimension4?: string; // email
	dimension5?: string; // role
}

const App = (props: AppProps) => {
	const {
		profile,
		location,
		analytics,
		push,
		organization,
		setDummyState,
		setUserAccessPermission,
		fetchOrganization,
		fetchFeatureAccess,
		fetchProfile,
		clearProfile,
		verifyAuthCodeState,
		userEnrollmentStatus,
		setEnrollmentStatus,
		userAccessLoaded,
		resetUserAccessState,
		resetOrganization,
		resetFeatureAccess,
	} = props;

	const auth = useFirebaseAuth();
	const { user, isLoaded, signout } = auth;
	// PostHog user identification and page tracking
	const { isIdentified } = usePostHogIdentify();
	usePageTracking(isIdentified);
	useDocumentTitle();
	useGoogleMapsKey();
	const [freezeModal, setFreezeModal] = useState<boolean>(false);
	const featureLms = useSelector((state: RootState) => state.featureAccess.features.LMS);
	const isFeatureLoaded = useSelector((state: RootState) => state.featureAccess.isLoaded);
	// @ts-ignore
	const homePage = useSelector((state: RootState) => state.featureAccess.features.HOME_PAGE) || false;

	const handleDismissModal = () => {
		setFreezeModal(false);
	};

	const setupHotjarAttributes = (userAttributes: {
		id: string;
		email: string;
		phoneNumber: string;
		organization: string;
		role: string;
		versionID: string;
	}) => {
		// @ts-ignore
		window.__USER__ = userAttributes;
		// @ts-ignore
		if (window.hj) hj('identify', userAttributes.id, userAttributes);
	};
	const setupReactGAAttributes = (user: (typeof auth)['user'], profile: (typeof props)['profile'], version: string) => {
		let googleAnalyticsAttributes: GAAttribute = { dimension2: version };
		if (!isEmpty(user!.uid)) googleAnalyticsAttributes.userId = user!.uid;
		if (!isEmpty(profile.organization)) googleAnalyticsAttributes.dimension1 = profile.organization;
		if (!isEmpty(user!.uid)) googleAnalyticsAttributes.dimension3 = user!.uid;
		if (!isEmpty(user!.email)) googleAnalyticsAttributes.dimension4 = user!.email as string;
		if (!isEmpty(profile.role)) googleAnalyticsAttributes.dimension5 = profile.role;
		if (Object.keys(googleAnalyticsAttributes).length) {
			ReactGA.set(googleAnalyticsAttributes);
		}
	};

	/**
	 * Set up Sentry user identification for error tracking
	 */
	const setupSentryUser = (user: (typeof auth)['user'], profile: (typeof props)['profile']) => {
		if (user && !profile.isEmpty) {
			identifyUser({
				email: user.email || undefined,
				id: user.uid,
				displayName: profile.displayName,
				organizationId: profile.organization,
				role: profile.role,
			});
		}
	};

	// if url ends with / remove it
	React.useEffect(() => {
		const pathname = location.pathname;
		if (pathname !== '/' && /\/$/.test(pathname)) {
			push(pathname.slice(0, -1));
		}
	}, [location]);

	// Redirect user to login page if unauthenticated
	React.useEffect(() => {
		if (!isLoaded || user) {
			return;
		}

		clearProfile();

		// If on /loading, redirect directly to /login (happens after logout)
		if (location.pathname === '/loading') {
			push('/login');
			return;
		}

		// For other pages (except /setpassword and /login), store the URL and redirect
		if (!isSetPasswordRoute(location.pathname) && !isLoginRoute(location.pathname)) {
			// Store the current URL (including query params) to redirect back after login
			const currentUrl = location.pathname + location.search;
			const redirectParam = encodeURIComponent(currentUrl);
			push(`/login?redirect=${redirectParam}`);
		}
	}, [isLoaded, user, location, push, clearProfile]);

	// Load the session behind an authenticated user, and revalidate it on
	// navigation: feature access is read straight out of the store by the route
	// guards and the sidebar, and nothing else refreshes it.
	//
	// That route dependency is also the edge #2188 ran around: a rejected session
	// re-fetched here, the gate below rejected it again, and the teardown
	// navigated, which landed back here. What bounds it now is that the teardown
	// no longer navigates once the app is on /login - see `endRejectedSession`.
	// The cycle can turn at most once per navigation from elsewhere, and cannot
	// drive itself.
	React.useEffect(() => {
		if (!isLoaded || !user) {
			return;
		}

		// ADR-065 Phase 3: the profile arrives over REST from api-users, driven off Firebase
		// Auth resolving. This replaces react-redux-firebase's `/user/{uid}` subscription; the
		// "unloaded, then loaded" lifecycle consumers already handle is unchanged.
		//
		// The token is minted here from the resolved user rather than inside the service via
		// API.getFirebaseToken(): at this point `auth().currentUser` is still null, so that
		// helper returns '' and the request 401s, which empties the profile and signs the
		// session straight back out. audit-web's #164 passes the token explicitly for the
		// same reason. An empty token is dispatched on failure so the saga reports a failed
		// profile rather than leaving it unloaded forever.
		user
			.getIdToken()
			.then((token) => fetchProfile({ userID: user.uid, token }))
			.catch(() => fetchProfile({ userID: user.uid, token: '' }));

		if (userEnrollmentStatus) {
			setUserAccessPermission();
			fetchOrganization();
			fetchFeatureAccess();
		}
	}, [
		isLoaded,
		user,
		location,
		userEnrollmentStatus,
		setUserAccessPermission,
		fetchOrganization,
		fetchFeatureAccess,
		fetchProfile,
	]);

	React.useEffect(() => {
		if (user) {
			setDummyState(user.uid);
		}
	}, [user, setDummyState]);

	React.useEffect(() => {
		if (!isActiveOrganization(organization.organization?.clientStatus)) {
			setFreezeModal(true);
		}
	}, [organization]);

	/**
	 * Ends a session the app cannot serve and returns the user to the login
	 * screen with a reason. The teardown itself - including why emptying the
	 * profile is the part that stops it repeating - lives in
	 * `endRejectedSession`, which is tested.
	 */
	const rejectSession = React.useCallback(
		(errorKey: RejectedSessionKey) => {
			endRejectedSession(errorKey, {
				clearProfile,
				resetUserAccessState,
				resetOrganization,
				resetFeatureAccess,
				signout,
				currentPathname: () => history.location.pathname,
				navigateToLogin: () => push('/login'),
				onSignoutError: (error) =>
					logErrorWithContext(error, 'reject_session_signout_failed', {
						component: 'App.tsx',
						errorKey,
					}),
			});
		},
		[clearProfile, resetUserAccessState, resetOrganization, resetFeatureAccess, signout, push],
	);

	// Location side effect on user authenticated
	// eslint-disable-next-line complexity
	React.useEffect(() => {
		if (!user || profile.isEmpty) {
			return;
		}

		// Whether this session can be served at all - including the ordering the
		// checks have to happen in - lives in `evaluateSession`, which is pure and
		// tested. See its doc for why the activation check cannot sit behind the
		// access-loaded gate.
		const gate = evaluateSession(profile, location.pathname, isFeatureLoaded && userAccessLoaded);
		if (gate.verdict === 'reject') {
			rejectSession(gate.reason);
			return;
		}
		if (gate.verdict === 'wait') {
			return;
		}

		const lmsType = organization?.organization?.lmsType;
		if (featureLms && (lmsType === 'standalone' || !props.adminAccess)) {
			if (!props.lmsRole) {
				return;
			}
		}

		setupHotjarAttributes({
			id: user.uid,
			email: user.email || '',
			phoneNumber: user.phoneNumber || '',
			organization: profile.organization,
			role: profile.role || '',
			versionID: version,
		});

		setupReactGAAttributes(user, profile, version);

		// Set up Sentry user identification for better error tracking
		setupSentryUser(user, profile);

		const isFromLogin = location.pathname === '/login';
		const isFromRoot = location.pathname === '/';

		if (isFromLogin) {
			if (moment().unix() - moment(auth.user?.metadata.lastSignInTime).unix() < 15) {
				const lastLoginDate = auth.user?.metadata!.lastSignInTime || new Date();
				const activity = new Common.Activity({
					entity: Common.enums.Entity.USER,
					entityID: user.uid,
					permission: Common.enums.Permission.View,
					type: Common.enums.ActivityType.LOGIN_USER,
					message: `User Login: ${auth.user?.displayName}`,
					timestamp: new Date(),
					snapshot: {
						userData: {
							email: auth.user?.email,
							userID: auth.user?.uid,
							displayName: auth.user?.displayName,
						},
						lastSignInTime: new Date(lastLoginDate),
						platform: 'admin',
					},
				});
				try {
					createNewActivity(activity);
				} catch (err) {
					throw new Error(`Activity error, ${activity}`);
				}
			}
		}
		if (isFromLogin || isFromRoot) {
			if (organization.organization && isActiveOrganization(organization.organization?.clientStatus)) {
				// Check if there's a redirect parameter from the original URL user tried to access
				const searchParams = new URLSearchParams(location.search);
				const redirectPath = searchParams.get('redirect');

				// If valid redirect path exists, redirect there instead of default home
				const safePath = redirectPath && isValidRedirectPath(redirectPath);
				if (safePath) {
					push(safePath);
					return;
				}

				// Otherwise, follow the existing default redirect logic
				if (featureLms && (lmsType === 'standalone' || !props.adminAccess)) {
					if ((isMobile || window.matchMedia('(max-width: 768px)').matches) && props.lmsRole === 'learner') {
						push(LMS_ROUTE.COURSE_AND_SYLLABUS_CATALOG);
						return;
					}
					push(LMS_ROUTE.DASHBOARD);
					return;
				}

				if (homePage) {
					push('/home');
				} else {
					push('/analytics/executive');
				}
				return;
			}
		}

		/**
		 * Fallback if some trying to access `/analytics/auditorDetails` directly
		 */
		const isAccessingAuditorDetail = location.pathname === '/analytics/auditorDetails/';
		const isQueryAllUser = analytics.selectedAuditor === '_all_';
		if (isAccessingAuditorDetail && isQueryAllUser) {
			if (isActiveOrganization(organization.organization?.clientStatus)) {
				push('/analytics/auditors');
			}
		}
	}, [
		user,
		location,
		analytics,
		profile,
		push,
		signout,
		organization,
		props.adminAccess,
		isFeatureLoaded,
		featureLms,
		props.lmsRole,
		userAccessLoaded,
		rejectSession,
	]);

	const authLoaded = user && isLoaded;

	const shouldShowTransitionModal = useMemo(() => {
		return (
			!verifyAuthCodeState.loading &&
			!!verifyAuthCodeState.tokenInfo.clientToken &&
			!!verifyAuthCodeState.tokenInfo.internalToken &&
			!verifyAuthCodeState.error
		);
	}, [verifyAuthCodeState.loading, verifyAuthCodeState.tokenInfo, verifyAuthCodeState.error]);

	const checkIsSandbox = async () => {
		if (!user) return;
		const res = await user.getIdTokenResult();
		props.setUserIsSandbox(!!res?.claims?.isSandboxAccount);
	};

	React.useEffect(() => {
		setEnrollmentStatus();
	}, []);

	React.useEffect(() => {
		checkIsSandbox();
	}, [user]);

	return (
		<AppErrorBoundary>
			<ConnectedRouter history={history}>
				<TransitionModal
					isVisible={shouldShowTransitionModal}
					orgName={verifyAuthCodeState.tokenInfo.organizationName}
				/>
				{/* @deprecated Please use [containerId=toast-message-container] to apply new design to new toasts */}
				<ToastContainer
					enableMultiContainer
					hideProgressBar
					autoClose={5000}
					toastClassName="toast"
					bodyClassName="toast-body"
					style={{ zIndex: 999999 }}
				/>
				<ToastContainer
					enableMultiContainer
					containerId="toast-message-container"
					hideProgressBar
					autoClose={5000}
					toastClassName="toast-message"
					bodyClassName="toast-message-body"
					style={{ zIndex: 999999 }}
				/>

				<FreezeAccountModal isVisible={freezeModal} handleDismissModal={handleDismissModal} />
				{authLoaded &&
				profile.isLoaded &&
				!isSetPasswordRoute(location.pathname) &&
				profile.status !== 'fresh' &&
				isActiveOrganization(organization.organization?.clientStatus) ? (
					<Switch>
						<Route path="/loading" component={LoadingPage} />
						<Route path="/login" component={LoginPage} />
						<Route path="/setpassword" component={SetPasswordPage} />

						{profile.role === 'superadmin' ? <SuperadminRoute /> : <AdminRoute />}

						<Route component={NotFound} />
					</Switch>
				) : (
					<Switch>
						<Route exact path="/" component={LoadingPage} />
						<Route path="/login" component={LoginPage} />
						<Route path="/setpassword" component={SetPasswordPage} />
						<Route component={LoadingPage} />
					</Switch>
				)}
				{authLoaded && profile && profile.email ? (
					<FreshChat
						token={freshChatConfig.key}
						faqTags={freshChatConfig.faqTags}
						onInit={(widget: any) => {
							if (profile?.email) {
								widget.user.setProperties({
									email: profile.email,
									first_name: profile.displayName,
									phone: profile.phoneNumber,
								});
							}
						}}
					/>
				) : null}
			</ConnectedRouter>
		</AppErrorBoundary>
	);
};

const mapStateToProps = (state: RootState) => ({
	auth: state.firebase.auth,
	profile: selectProfile(state),
	location: state.router.location,
	analytics: state.analytics,
	organization: state.organization,
	verifyAuthCodeState: state.clientLogin.verifyAuthCode,
	userEnrollmentStatus: state.userEnrollment.enrollmentStatus,
	adminAccess: state.userAccess.admin.sites.all.permissions.edit,
	lmsRole: state.userAccess.lmsRole,
	userAccessLoaded: state.userAccess.isLoaded,
});

const mapDispatchToProps = {
	fetchProfile: fetchProfileAsync.request,
	clearProfile,
	setDummyState,
	push,
	setUserAccessPermission,
	resetUserAccessState,
	resetOrganization,
	resetFeatureAccess,
	fetchOrganization: fetchOrganizations.request,
	fetchFeatureAccess: fetchFeatureAccess.request,
	setEnrollmentStatus,
	setUserIsSandbox,
};
// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface OwnProps {}
type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = typeof mapDispatchToProps;
type EnhancedProps = StateProps & DispatchProps;

type AppProps = OwnProps & EnhancedProps;

const enhance: InferableComponentEnhancerWithProps<EnhancedProps, OwnProps> = connect(
	mapStateToProps,
	mapDispatchToProps,
);

export default enhance(Sentry.withProfiler(App));
